import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPatch } from "../../lib/api-client";
import type { RepeatWithSeat } from "./useReservations";

export interface User {
  email: string;
  name: string;
  role: "user" | "admin";
  createdAt: string;
  updatedAt: string;
}

interface UsersResponse {
  users: User[];
  total: number;
}

interface UserResponse {
  user: User;
}

export interface FutureReservation {
  date: string;
  seatId: number;
  userName: string;
  repeatId?: string;
  seat: { seatId: number; label: string } | null;
}

interface FutureReservationsResponse {
  reservations: FutureReservation[];
  repeats: RepeatWithSeat[];
}

interface BulkCancelResponse {
  deletedReservations: number;
  deletedRepeats: number;
  failedReservations: number;
  failedRepeats: number;
}

export function useAdminUsers(search?: string) {
  return useQuery({
    queryKey: ["admin", "users", search],
    queryFn: async () => {
      const params = search ? `?search=${encodeURIComponent(search)}` : "";
      const response = await apiGet<UsersResponse>(`/admin/users${params}`);
      return response;
    },
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { email: string; role: "user" | "admin" }) => {
      return apiPatch<UserResponse>(
        `/admin/users/${encodeURIComponent(data.email)}`,
        {
          role: data.role,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => {
      return apiDelete<{ message: string }>(
        `/admin/users/${encodeURIComponent(email)}`
      );
    },
    onSettled: () => {
      // 途中で失敗した場合も、一部の予約は削除済みの可能性がある。
      queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
    },
  });
}

export function useAdminUserFutureReservations(email: string | undefined) {
  return useQuery({
    queryKey: ["admin", "users", email, "future-reservations"],
    queryFn: async () => {
      if (!email) {
        throw new Error("email is required");
      }
      const response = await apiGet<FutureReservationsResponse>(
        `/admin/users/${encodeURIComponent(email)}/future-reservations`
      );
      return response;
    },
    enabled: Boolean(email),
  });
}

export function useBulkCancelUserFutureReservations() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (email: string) => {
      return apiDelete<BulkCancelResponse>(
        `/admin/users/${encodeURIComponent(email)}/future-reservations`
      );
    },
    onSuccess: (_data, email) => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "users", email, "future-reservations"],
      });
      queryClient.invalidateQueries({ queryKey: ["reservations"] });
    },
  });
}
